import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, PageHeader } from "@/components/kit";
import {
  DownloadSimple,
  HourglassMedium,
  CheckSquareOffset,
  Receipt,
  UserCircle,
  Coins,
  Users,
  BookOpen,
} from "@phosphor-icons/react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportsPage() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [date, setDate] = useState(today);
  const [busy, setBusy] = useState("");

  const canReports = ["super_admin", "branch_admin", "staff"].includes(user.role);
  if (!canReports) {
    return <div className="p-8 text-slate-500">Reports are available to admins and staff only.</div>;
  }

  const download = async (path, filenameFallback) => {
    setBusy(path);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${REACT_APP_BACKEND_URL}/api${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const m = /filename="?([^";]+)"?/.exec(disposition);
      const filename = m ? m[1] : filenameFallback;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Download failed: " + e.message);
    } finally {
      setBusy("");
    }
  };

  const cards = [
    {
      key: "maturity_upcoming",
      title: "Pre-maturity Report",
      subtitle: "Deposits maturing in the next 30 days",
      icon: HourglassMedium,
      tone: "amber",
      path: (fmt) => `/reports/maturity?kind=upcoming&days=30&fmt=${fmt}`,
      file: "maturity_upcoming",
    },
    {
      key: "maturity_matured",
      title: "Maturity Report",
      subtitle: "Deposits already matured, pending payout/renewal",
      icon: HourglassMedium,
      tone: "rose",
      path: (fmt) => `/reports/maturity?kind=matured&fmt=${fmt}`,
      file: "maturity_matured",
    },
    {
      key: "loan_upcoming",
      title: "Pre-loan Closure Report",
      subtitle: "Loans reaching final EMI in the next 30 days",
      icon: CheckSquareOffset,
      tone: "amber",
      path: (fmt) => `/reports/loan-closure?kind=upcoming&days=30&fmt=${fmt}`,
      file: "loan_closure_upcoming",
    },
    {
      key: "loan_closed",
      title: "Loan Closure Report",
      subtitle: "Loans already closed (regular or pre-closed)",
      icon: CheckSquareOffset,
      tone: "emerald",
      path: (fmt) => `/reports/loan-closure?kind=closed&fmt=${fmt}`,
      file: "loan_closure_closed",
    },
    {
      key: "expenses",
      title: "Expenses Report",
      subtitle: "All office & travel expenses in a date range",
      icon: Receipt,
      tone: "sky",
      path: (fmt) => `/reports/expenses?from_date=${from}&to_date=${to}&fmt=${fmt}`,
      file: "expenses",
      needsRange: true,
    },
    {
      key: "commissions",
      title: "Agent Commission Report",
      subtitle: "Commissions & salaries paid to agents",
      icon: UserCircle,
      tone: "sky",
      path: (fmt) => `/reports/agent-commissions?from_date=${from}&to_date=${to}&fmt=${fmt}`,
      file: "agent_commissions",
      needsRange: true,
    },
    {
      key: "cash_book",
      title: "Cash Book",
      subtitle: "Cash in / cash out ledger with running balance",
      icon: Coins,
      tone: "emerald",
      path: (fmt) => `/reports/cash-book?from_date=${from}&to_date=${to}&fmt=${fmt}`,
      file: "cash_book",
      needsRange: true,
    },
    {
      key: "daily",
      title: "Daily Collection Sheet",
      subtitle: "Cash / Card / UPI / NEFT / RTGS + denomination breakup",
      icon: Coins,
      tone: "emerald",
      path: (fmt) => `/reports/daily-collections?date=${date}&fmt=${fmt}`,
      file: "daily_collections",
      needsDate: true,
    },
    {
      key: "agent_daily",
      title: "Agent Daily Collection Report",
      subtitle: "Per-agent totals for one day, split by method",
      icon: Users,
      tone: "sky",
      path: (fmt) => `/reports/agent-daily-collections?date=${date}&fmt=${fmt}`,
      file: "agent_daily_collections",
      needsDate: true,
    },
  ];

  const toneStyles = {
    amber: "border-amber-200 bg-amber-50/40",
    rose: "border-rose-200 bg-rose-50/40",
    emerald: "border-emerald-200 bg-emerald-50/40",
    sky: "border-sky-200 bg-sky-50/40",
  };
  const toneIcon = {
    amber: "text-amber-700 bg-amber-100",
    rose: "text-rose-700 bg-rose-100",
    emerald: "text-emerald-700 bg-emerald-100",
    sky: "text-sky-700 bg-sky-100",
  };

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up" data-testid="reports-page">
      <PageHeader
        title="Reports"
        subtitle="Download CSV or Excel exports. Use the filters where applicable."
      />

      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Date range</div>
            <div className="flex gap-2 items-center">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} testId="reports-from" />
              <span className="text-slate-400 text-sm">→</span>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} testId="reports-to" />
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Single date</div>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} testId="reports-date" />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const csv = c.path("csv");
          const xlsx = c.path("xlsx");
          const useDate = c.needsDate ? date : "all";
          const useRange = c.needsRange ? `${from}_${to}` : "";
          const fileName = `${c.file}_${useDate}${useRange}.csv`;
          return (
            <Card key={c.key} className={`p-5 border ${toneStyles[c.tone]}`} data-testid={`report-card-${c.key}`}>
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneIcon[c.tone]}`}>
                  <Icon size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="heading text-base font-semibold text-slate-900">{c.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.subtitle}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => download(csv, fileName)}
                  disabled={busy === csv}
                  data-testid={`download-csv-${c.key}`}
                  className="flex-1 justify-center"
                >
                  <DownloadSimple size={14} weight="bold" /> {busy === csv ? "…" : "CSV"}
                </Button>
                <Button
                  onClick={() => download(xlsx, fileName.replace(".csv", ".xlsx"))}
                  disabled={busy === xlsx}
                  data-testid={`download-xlsx-${c.key}`}
                  className="flex-1 justify-center"
                >
                  <DownloadSimple size={14} weight="bold" /> {busy === xlsx ? "…" : "Excel"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="mt-6 p-5 border border-slate-200 bg-slate-50/50">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-700 bg-slate-200">
            <BookOpen size={20} weight="fill" />
          </div>
          <div>
            <div className="heading text-base font-semibold text-slate-900">Passbook / Ledger Print</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Open any Savings or Loan account → click <b>Print</b> for a one-click passbook.
              Or open a customer's page → <b>Statement</b> for a combined CSV/Excel.
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
